import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { SessionContext } from "./SessionContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./App.css";
import Api from "./Api";

import Layout from "./parts/Layout";
import RequireAuth from "./parts/RequireAuth";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Maintenance from "./pages/Maintenance";
import NotFound from "./pages/NotFound";

import UsersList from "./pages/Users/List";
import UsersCreate from "./pages/Users/Create";
import UsersUpdate from "./pages/Users/Update";

import ClientsList from "./pages/Clients/List";
import ClientsCreate from "./pages/Clients/Create";
import ClientsUpdate from "./pages/Clients/Update";
import ClientsDelete from "./pages/Clients/Delete";

import MyQuotesList from "./pages/MyQuotes/List";
import MyQuotesCreate1 from "./pages/MyQuotes/Create1";
import MyQuotesCreate2 from "./pages/MyQuotes/Create2";
import MyQuotesView from "./pages/MyQuotes/View";
import MyQuotesDuplicate from "./pages/MyQuotes/Duplicate";
import MyQuotesUpdate from "./pages/MyQuotes/Update";

import QuotesAdminList from "./pages/QuotesAdmin/List";
import QuotesAdminUpdate from "./pages/QuotesAdmin/Update";

import AuthorizationsList from "./pages/Authorizations/List";

import ContractsDashboard from "./pages/Contracts/Dashboard";
import ContractsView from "./pages/Contracts/View";

import InstallationsDashboard from "./pages/Installations/Dashboard";

import EquipmentsList from "./pages/Equipments/List";
import EquipmentsCreate from "./pages/Equipments/Create";
import EquipmentsUpdate from "./pages/Equipments/Update";

import FinanceHub from "./pages/Finance/Hub";
import FinanceRentReceipts from "./pages/Finance/RentReceipts";
import FinanceContractClients from "./pages/Finance/ContractClients";
import FinanceContractClient from "./pages/Finance/ContractClient";
import FinanceContractCreate from "./pages/Finance/ContractCreate";
import FinanceContract from "./pages/Finance/Contract";
import FinanceReceiptsClients from "./pages/Finance/ReceiptsClients";
import FinanceReceiptsClient from "./pages/Finance/ReceiptsClient";
import FinanceReceiptsCreate from "./pages/Finance/ReceiptsCreate";
import FinanceReport from "./pages/Finance/Report";

import TechnicalList from "./pages/Technical/List";
import TechnicalOrder from "./pages/Technical/Order";
import TechnicalOrderClient from "./pages/Technical/OrderClient";

import Stats from "./pages/Stats/Stats";

import ExternalQuote from "./pages/External/Quote";
import ExternalAuthorizeQuote from "./pages/External/AuthorizeQuote";
import ExternalRentReceipt from "./pages/External/RentReceipt";
import ExternalGenericReceipt from "./pages/External/GenericReceipt";
import LegacyQuote from "./pages/Legacy/Quote";
import LegacyRentReceipt from "./pages/Legacy/RentReceipt";

const version = "59";

const checkVersion = async (onDiff: () => void) => {
  const apiVersion = await Api.version().catch(() => "-1");
  if (apiVersion !== version) {
    onDiff();
    return;
  }
  setTimeout(() => checkVersion(onDiff), 20 * 1000);
};

const App = () => {
  const [maintenance, setMaintenance] = useState(false);
  const sessionContext = useContext(SessionContext);

  useEffect(() => {
    checkVersion(() => setMaintenance(true));
  }, []);

  if (maintenance) {
    return <Maintenance />;
  }

  if (sessionContext.loading) {
    return (
      <div className="h-screen flex items-center">
        <div className="w-full text-center text-4xl">
          <FontAwesomeIcon icon="circle-notch" spin={true} />
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Layout version={version} />}>
          <Route path="/login" element={<Login />} />

          <Route element={<RequireAuth />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/usuarios" element={<UsersList />} />
            <Route path="/usuarios/criar" element={<UsersCreate />} />
            <Route path="/usuarios/:uuid" element={<UsersUpdate />} />

            <Route path="/clientes" element={<ClientsList />} />
            <Route path="/clientes/criar" element={<ClientsCreate />} />
            <Route path="/clientes/:uuid" element={<ClientsUpdate />} />
            <Route path="/clientes/deletar" element={<ClientsDelete />} />

            <Route path="/meus-orcamentos" element={<MyQuotesList />} />
            <Route
              path="/meus-orcamentos/criar"
              element={<MyQuotesCreate1 />}
            />
            <Route
              path="/meus-orcamentos/criar/:clientUuid"
              element={<MyQuotesCreate2 />}
            />
            <Route
              path="/meus-orcamentos/duplicar/:uuid"
              element={<MyQuotesDuplicate />}
            />
            <Route
              path="/meus-orcamentos/alterar/:uuid"
              element={<MyQuotesUpdate />}
            />

            <Route path="/aprovar-orcamentos" element={<QuotesAdminList />} />
            <Route
              path="/aprovar-orcamentos/alterar/:uuid"
              element={<QuotesAdminUpdate />}
            />

            <Route path="/autorizacoes" element={<AuthorizationsList />} />

            <Route path="/contratos" element={<ContractsDashboard />} />

            <Route path="/instalacoes" element={<InstallationsDashboard />} />

            <Route path="/estatisticas" element={<Stats />} />

            <Route path="/equipamentos" element={<EquipmentsList />} />
            <Route path="/equipamentos/criar" element={<EquipmentsCreate />} />
            <Route path="/equipamentos/:uuid" element={<EquipmentsUpdate />} />

            <Route path="/financeiro" element={<FinanceHub />} />
            <Route path="/financeiro/notas" element={<FinanceRentReceipts />} />
            <Route
              path="/financeiro/contratos/clientes"
              element={<FinanceContractClients />}
            />
            <Route
              path="/financeiro/contratos/cliente/:uuid"
              element={<FinanceContractClient />}
            />
            <Route
              path="/financeiro/contratos/criar/:clientUuid"
              element={<FinanceContractCreate />}
            />
            <Route
              path="/financeiro/contrato/:uuid"
              element={<FinanceContract />}
            />
            <Route
              path="/financeiro/recibos/clientes"
              element={<FinanceReceiptsClients />}
            />
            <Route
              path="/financeiro/recibos/cliente/:uuid"
              element={<FinanceReceiptsClient />}
            />
            <Route
              path="/financeiro/recibos/criar/:clientUuid"
              element={<FinanceReceiptsCreate />}
            />

            <Route path="/os" element={<TechnicalList />} />
          </Route>
        </Route>

        <Route element={<RequireAuth />}>
          <Route
            path="/financeiro/relatorio/:companyUuid/:year/:month"
            element={<FinanceReport />}
          />
          <Route
            path="/aprovar-orcamentos/visualizar/:uuid"
            element={<MyQuotesView />}
          />
          <Route
            path="/meus-orcamentos/visualizar/:uuid"
            element={<MyQuotesView />}
          />
          <Route path="/contratos/:type/:uuid" element={<ContractsView />} />
          <Route path="/os/autorizacao/:uuid" element={<TechnicalOrder />} />
          <Route path="/os/cliente/:uuid" element={<TechnicalOrderClient />} />
        </Route>

        <Route path="/externo/proposta/:hash" element={<ExternalQuote />} />
        <Route
          path="/externo/autorizar-proposta/:hash"
          element={<ExternalAuthorizeQuote />}
        />
        <Route
          path="/externo/nota-fatura/:hash"
          element={<ExternalRentReceipt />}
        />
        <Route
          path="/externo/recibo/:hash"
          element={<ExternalGenericReceipt />}
        />
        <Route path="/public/orcamento/" element={<LegacyQuote />} />
        <Route path="/public/notafatura/" element={<LegacyRentReceipt />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
